import { LitElement, html } from 'lit';
import { customElement, state } from 'lit/decorators.js';

import '@shoelace-style/shoelace/dist/components/card/card.js';
import '@shoelace-style/shoelace/dist/components/button/button.js';
import '@shoelace-style/shoelace/dist/components/icon/icon.js';

import { styles as sharedStyles } from '../../styles/shared-styles';
import { styles } from './home-styles';
import { reference, write } from '../../language/lang';
import { WithUserProfileConsumer } from '../../mixins/with-user-profile-consumer';
import { writeMdHTML } from '../../utility/markdown';
import {
  getRecentModules,
  RecentModuleEntry,
} from '../../services/recent-modules-service';
import {
  ModuleMeta,
  resolveVisibleModuleIds,
} from '../../modules/registry/module-registry';
import { terms } from '../../language/terms';

/** Combined type for display */
interface RecentModuleDisplay {
  entry: RecentModuleEntry;
  meta: ModuleMeta;
}

@customElement('app-home')
export class AppHome extends WithUserProfileConsumer(LitElement) {
  static styles = [sharedStyles, styles];

  @state() private recentModules: RecentModuleDisplay[] = [];

  firstUpdated() {
    console.log('This is your home page');
    void this.loadRecentModules();
  }

  private async loadRecentModules() {
    const userId = this.userProfile?.userId;
    if (!userId) return;

    const entries = await getRecentModules(userId, 5);

    // The visibility filter this depends on now lives in the registry as resolveVisibleModuleIds,
    // shared with the workflow menu. The rule it enforces is unchanged and is stated there: a recent
    // entry outlives the module being archived or access being withdrawn, so it must be resolved
    // through visibility or a withdrawn module stays reachable from a quick link (issue #302).
    const byId = new Map(entries.map((entry) => [entry.moduleId, entry]));
    this.recentModules = resolveVisibleModuleIds(
      entries.map((entry) => entry.moduleId),
      { access: this.access, isLoggedIn: this.isAuthenticated }
    ).map((meta) => ({ entry: byId.get(meta.id)!, meta }));
  }

  share() {
    if ('share' in navigator) {
      void navigator.share({
        title: reference('app displayName'),
        text: write`Check out {{app displayName}}, {{company}}'s tools for its {{term member.lowerPlural}} across the glass and window industry.`,
        url: reference('app url'),
      });
    }
  }

  private renderRecentModules() {
    if (this.recentModules.length === 0) {
      return html`
        <div class="recent-empty">
          <sl-icon name="clock-history"></sl-icon>
          <p>No recently used modules yet.</p>
          <p>Visit some modules to see them here!</p>
        </div>
      `;
    }

    return html`
      <div class="recent-modules-list">
        ${this.recentModules.map(
          ({ meta }) => html`
            <a class="recent-module-item" href="/${meta.route}">
              <sl-icon name="${meta.iconName || 'box'}"></sl-icon>
              <div class="recent-module-info">
                <div class="recent-module-title">${meta.title}</div>
                <div class="recent-module-category">${meta.category}</div>
              </div>
              <sl-icon name="chevron-right"></sl-icon>
            </a>
          `
        )}
      </div>
    `;
  }

  render() {
    return html`
      <div class="banner-container">
        <img
          src="/assets/images/agwa-background-header-1.jpg"
          alt="${terms.agwa.value} Banner"
          class="banner-image"
        />
      </div>
      <div class="home-content">
        <sl-card class="welcome-card">
          <h2>Welcome to ${reference('app displayName')}</h2>
          <!--
            Says what the application holds and what decides what a person can open, and nothing
            more. The copy it replaces was sample marketing text about "member-exclusive resources"
            that nobody had edited since it was pasted in (architect, 2026-09-08: "redo the home
            page copy ... it was sample copy i added and no one edited"). It speaks of AGWA's
            MEMBERS on purpose, in the membership sense the association thinks in (architect, same
            day: "member may be the correct use, in the way they think of members"), through the
            glossary's own member term rather than a hard-coded word. Every domain noun here is a
            term or a reference, never a word of its own.
          -->
          <div class="welcome-content">
            ${writeMdHTML`
### What is here

{{app displayName}} is where **{{company displayName}} ({{company}})** and its {{term member.lowerPlural}} keep the records that stand behind accredited glass and window {{term product.lowerPlural}}:

* {{term glassAccreditation.value}} certificates held by {{term primeGlassManufacturer.lower}} {{term loginSite.lowerPlural}}
* The {{term productRegistry.value}}: {{term product.lowerPlural}} and the test reports that support them
* {{term loginGroup.plural}}, their {{term loginSite.lowerPlural}}, and the {{term loginUser.lowerPlural}} who work at them

What you can open depends on your {{term loginGroup.lower}} and on the {{term userGroupDomain.lowerPlural}} its {{term loginSite.lowerPlural}} hold. The modules you used most recently are listed beside this card; the menu carries the rest.
            `}
          </div>
          ${
            'share' in navigator
              ? html`<sl-button
                  slot="footer"
                  variant="primary"
                  @click=${this.share}
                >
                  <sl-icon slot="prefix" name="share"></sl-icon>
                  ${write`Share {{app displayName}}`}
                </sl-button>`
              : null
          }
        </sl-card>

        <sl-card class="recent-card">
          <h2>Recent</h2>
          ${this.renderRecentModules()}
        </sl-card>
      </div>
    `;
  }
}
